import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Bell, Sparkles } from "lucide-react";
import SeoCopy from "@/components/SeoCopy";

export default function MapPage() {
  const [areas, setAreas] = useState([]);
  useEffect(() => { api.get("/service-areas/momentum").then((r) => setAreas(r.data)); }, []);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="font-fredoka text-3xl sm:text-4xl font-bold text-slate-900 mb-2 flex items-center gap-2" data-testid="map-title">
        <MapPin strokeWidth={2.5} className="text-green-600"/> Service Areas
      </h1>
      <p className="text-slate-600 mb-6">Where Bag Dash Kids runs (and where we're headed next).</p>

      <div className="grid sm:grid-cols-2 gap-4" data-testid="areas-list">
        {areas.map((a) => <AreaCard key={a.id} area={a}/>)}
      </div>

      {areas.length === 0 && <div className="card-clay p-8 text-center text-slate-400">No service areas published yet.</div>}

      <SeoCopy
        title="Where Bag Dash Kids serves"
        links={[
          { to: "/schedule", label: "Schedule a pickup" },
          { to: "/about", label: "Meet the Kids" },
          { to: "/contact", label: "Request a new area" },
          { to: "/policies", label: "Pricing & policies" },
        ]}
      >
        <p>Bag Dash Kids currently serves apartment and townhome communities in the <strong>Charlotte, NC</strong> area on Friday morning, Friday evening, Saturday morning, and Saturday evening pickup windows. Our flagship community is <strong>Hawthorne at Whitehall</strong>, and we expand to new neighborhoods when enough residents join the local waitlist.</p>
        <p>Want Bag Dash Kids in your building? Join the waitlist above with your email — we&apos;ll launch service the moment we hit a critical mass of neighbors. The kids personally walk the routes, so we keep service areas tight and well-supervised by Mom Manager Donja.</p>
      </SeoCopy>
    </div>
  );
}

function AreaCard({ area }) {
  const active = area.status === "active";
  return (
    <div className={`card-clay p-6 relative overflow-hidden ${active ? "bg-green-50 border-green-200" : ""}`} data-testid={`area-${area.id}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="font-fredoka text-2xl font-bold text-slate-900">{area.name}</div>
          <div className={`inline-flex items-center gap-1 mt-1 text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${active ? "bg-green-200 text-green-900" : "bg-amber-100 text-amber-900"}`}>
            {active ? <Sparkles size={12} strokeWidth={3}/> : <Bell size={12} strokeWidth={3}/>}
            {active ? "Active" : "Coming soon"}
          </div>
          {area.blurb && <p className="text-sm text-slate-600 mt-3">{area.blurb}</p>}
        </div>
        <MapPin className={active ? "text-green-600" : "text-amber-500"} size={28} strokeWidth={2.5}/>
      </div>

      {!active && (
        <>
          <div className="mt-4" data-testid={`momentum-${area.id}`}>
            <div className="flex items-center justify-between text-xs font-bold uppercase tracking-wider text-amber-900 mb-1">
              <span>Waitlist momentum</span>
              <span>{area.current || 0} / {area.target || 20}</span>
            </div>
            <div className="h-3 bg-white border-2 border-amber-200 rounded-full overflow-hidden">
              <div className="h-full bg-amber-500" style={{ width: `${area.pct || 0}%` }} />
            </div>
            <div className="text-xs text-amber-800 mt-1">{(area.target || 20) - (area.current || 0)} more neighbors to launch!</div>
          </div>
          <NotifyForm area={area.name}/>
        </>
      )}
    </div>
  );
}

function NotifyForm({ area }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await api.post("/waitlist", { name, email, area_name: area });
      setDone(true);
      toast.success("You're on the list! We'll notify you.");
    } catch { toast.error("Could not join waitlist"); }
    setSubmitting(false);
  };

  if (done) return <div className="mt-4 text-sm font-bold text-green-700">✅ You're on the waitlist for {area}.</div>;

  return (
    <form onSubmit={submit} className="mt-4 space-y-2" data-testid={`notify-form-${area}`}>
      <input className="input-clay h-10 px-3 w-full" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} data-testid={`notify-name-${area}`}/>
      <input className="input-clay h-10 px-3 w-full" type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid={`notify-email-${area}`}/>
      <button disabled={submitting} className="btn-push-amber w-full py-2 text-sm" data-testid={`notify-submit-${area}`}>
        {submitting ? "Adding…" : "Notify Me"}
      </button>
    </form>
  );
}
